/**
 * solar-api 타입드 클라이언트.
 *
 * 레거시는 화면마다 jQuery `$.ajax` 를 직접 호출하고 `returnCode` 를 각자
 * 해석했다(200 정상 / 202 정보없음 / 203 상태불일치 / 402 실패 / 403 미인증).
 * 여기서는 표준 HTTP 상태를 쓰고, 토큰 재발급도 이 계층에서만 처리한다.
 */

import { clearToken, getToken, setToken } from '@solar/native-bridge';

export interface ApiConfig {
  baseUrl: string;
  onUnauthorized?: () => void;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

// --------------------------------------------------------------------- 타입
export interface UserProfile {
  id: number;
  user_id: string;
  name: string | null;
  type: number;
  build_company_id: number | null;
  /**
   * 역할의 권한 코드(서버 app/core/permissions.py). 관리자 메뉴 노출에 쓴다.
   * 구버전 서버 응답에는 없으므로 optional — 비어 있으면 화면이 전체 메뉴를 보여준다.
   */
  permissions?: string[];
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  user: UserProfile;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/** app/schemas/order.py 의 OrderListItem 과 1:1 대응. */
export interface OrderListItem {
  id: number;
  user_name: string | null;
  user_phone: string | null;
  addr_full: string | null;
  addr2: string | null;
  building_name: string | null;
  apart_number: string | null;
  apt_type: number | null;
  install_date: string | null;
  install_complete: string | null;
  install_complete_date: string | null;
  state_type: number;
  payment: number | null;
  payment_check: string | null;
  engineer_id: number | null;
  temp_save_yn: string | null;
  engineer_name: string | null;
  build_team_name: string | null;
  product_name: string | null;
  company_id: number | null;
  capacity: number | null;
}

export interface PhotoOut {
  slot: number;
  url: string;
  taken_at: string | null;
}

export interface MemoOut {
  id: number;
  memo: string | null;
  users_id: number | null;
  user_name: string | null;
  created_at: string | null;
}

export interface OrderDetail extends OrderListItem {
  /** 철거 3필드 — 서버 계약 확정, 배포 순서에 따라 잠시 안 내려올 수 있어 선택형. */
  uninstall_status?: 'Y' | 'N' | null;
  uninstall_date?: string | null;
  uninstall_payment?: number | null;
  user_birth: string | null;
  user_home: string | null;
  user_email: string | null;
  new_addr: string | null;
  addr1: string | null;
  addr3: string | null;
  addr4: string | null;
  addr5: string | null;
  post_code: string | null;
  place_install: string | null;
  price: number | null;
  /** 정부지원금(국비) — 2026 보급사업부터 시/구 보조금과 별도 집계. 배포 순서상 선택형. */
  price_gov?: number | null;
  price_si: number | null;
  price_borough: number | null;
  payment_real: number | null;
  payment_type: string | null;
  install_type: string | null;
  install_slope_angle: number | null;
  install_slope_direction: string | null;
  inverter_print: number | null;
  inverter_print_time: string | null;
  motion_check_date: string | null;
  state_doc: number | null;
  state_gu: number | null;
  etc: string | null;
  photos: PhotoOut[];
  memos: MemoOut[];
  contract_count: number;
}

/** 레거시 orderList 의 `install_type` 파라미터를 대체하는 필터. */
export type OrderStatusFilter = 'all' | 'completed' | 'incomplete' | 'canceled' | 'unpaid';

export interface OrderListQuery {
  status?: OrderStatusFilter;
  install_date_from?: string;
  install_date_to?: string;
  keyword?: string;
  assigned_to_me?: boolean;
  include_canceled?: boolean;
  page?: number;
  size?: number;
}

export interface OrderUpdate {
  install_date?: string;
  install_type?: string;
  install_complete?: 'Y' | 'N' | 'C';
  etc?: string;
  payment_check?: 'Y' | 'N';
  payment_type?: string;
  payment?: number;
  inverter_print?: number;
  inverter_print_time?: string;
  motion_check_date?: string;
  install_slope_angle?: number;
  install_slope_direction?: string;
}

/**
 * `POST /api/v1/orders` 본문 — app/schemas/order.py 의 OrderCreate 와 1:1 대응.
 * 총사업비(price)·자부담(payment)은 서버가 상품가 기준으로 확정하므로 보내지 않는다.
 */
export interface OrderCreate {
  /** 에너지공사(1/2) 계정만 지정. 보급업체 계정은 서버가 자기 회사로 강제한다. */
  company_id?: number;
  user_name: string;
  user_phone: string;
  new_addr: string;
  def_product_id: number;
  user_birth?: string;
  user_home?: string;
  user_email?: string;
  addr_full?: string;
  addr1?: string;
  addr2?: string;
  addr3?: string;
  addr4?: string;
  addr5?: string;
  post_code?: string;
  building_name?: string;
  apt_type?: number;
  com_year?: string;
  host?: string;
  host_relation?: string;
  host_install_agree?: 'Y' | 'N';
  req_type?: 'host' | 'tenant';
  install_date?: string;
  install_type?: 'hook' | 'attachment' | 'slope_hook';
  price_si?: number;
  price_borough?: number;
  /** SH 보조금 — apt_type 2/7(SH 임대·재개발) 전용. */
  price_sh?: number;
  payment_etc?: number;
  payment_type?: string;
  payment_check?: 'Y' | 'N';
  team_id?: number;
  engineer_id?: number;
  etc?: string;
}

export interface UninstallListQuery {
  keyword?: string;
  page?: number;
  size?: number;
}

/** `POST /api/v1/orders/{id}/uninstall` — 레거시 OrderUninstall 파리티. */
export interface UninstallRequest {
  uninstall_status?: 'Y' | 'N';
  uninstall_payment?: number;
  uninstall_date?: string;
}

/** 일괄 처리 결과 — 전체 성공 아니면 전체 실패(부분 성공 없음). */
export interface BatchResult {
  updated_ids: number[];
}

export interface AsListItem {
  id: number;
  build_order_id: number;
  build_company_id: number | null;
  engineer_id: number | null;
  state: number;
  is_complete: string | null;
  etc: string | null;
  as_date: string | null;
  as_due_date: string | null;
  com_date: string | null;
  user_name: string | null;
  user_phone: string | null;
  addr_full: string | null;
  engineer_name: string | null;
  state_date: string | null;
}

export interface AsDetail extends AsListItem {
  engineer_memo: string | null;
  memos: MemoOut[];
}

export interface AsListQuery {
  state?: number;
  keyword?: string;
  assigned_to_me?: boolean;
  page?: number;
  size?: number;
}

/** `GET /api/v1/users` 응답 항목 — A/S 배정 화면의 기사(role=4) 검색에 쓴다. */
export interface UserListItem {
  id: number;
  user_id: string;
  name: string | null;
  type: number;
  build_company_id: number | null;
  build_team_id: number | null;
  /** app/schemas/user.py UserOut.status — 'normal' | 'stop' 등 문자열. */
  status: string | null;
}

export interface UserListQuery {
  role?: number;
  keyword?: string;
  page?: number;
  size?: number;
}

/** `GET /api/v1/users/{id}` — app/schemas/user.py 의 UserDetail 과 1:1. */
export interface UserDetail {
  id: number;
  user_id: string;
  name: string | null;
  email: string | null;
  contact: string | null;
  type: number;
  status: string | null;
  build_company_id: number | null;
  build_team_id: number | null;
  addr_gu_id: number | null;
}

/** `POST /api/v1/users` 본문 — UserCreate 스키마와 1:1. 비밀번호 규칙은 서버가 검증(422). */
export interface UserCreateBody {
  user_id: string;
  password: string;
  name: string;
  email?: string;
  contact?: string;
  type: number;
  status?: string;
  build_company_id?: number;
  build_team_id?: number;
  addr_gu_id?: number;
}

/** `PATCH /api/v1/users/{id}` 본문 — UserUpdate 스키마와 1:1. */
export interface UserUpdateBody {
  name?: string;
  email?: string;
  contact?: string;
  type?: number;
  status?: string;
  build_team_id?: number | null;
  addr_gu_id?: number | null;
}

// --- 정보 관리(마스터) — app/schemas/define.py 와 1:1 ----------------------
/** 시/도 목록 항목 — [시]보조금 등록 셀렉트용. */
export interface AddrSiOut {
  id: number;
  name: string;
}

/** 자치구 목록 항목 — 구 관리자 자치구 지정 등 셀렉트용. */
export interface AddrGuOut {
  id: number;
  addr_si_id: number;
  name: string;
}

export interface SubsidyOut {
  id: number;
  parent_id: number | null;
  addr_si_id: number;
  addr_gu_id: number | null;
  amount: number;
  amount2: number | null;
  sh_amount: number | null;
  gu_state: string | null;
  max_amount: number | null;
  addr_si_name: string | null;
  addr_gu_name: string | null;
}

export interface SubsidyListQuery {
  /** si=[시]보조금(addr_gu_id IS NULL), gu=[구]보조금 */
  kind?: 'si' | 'gu';
  addr_si_id?: number;
  keyword?: string;
  page?: number;
  size?: number;
}

export interface SubsidyCreateBody {
  addr_si_id?: number;
  addr_gu_id?: number;
  amount: number;
  amount2?: number;
  sh_amount?: number;
  gu_state?: string;
  max_amount?: number;
}

export interface SubsidyUpdateBody {
  amount?: number;
  amount2?: number;
  sh_amount?: number;
  gu_state?: string;
  max_amount?: number;
}

/**
 * 연도별 공고 단가 행 (def_subsidy_gu_year 신규 계열) — 시민 견적의 재원 기준.
 * addr_si null 은 전국 기본, install_type/variant null 은 유형 무관/일반.
 */
export interface SubsidyRateOut {
  id: number;
  year: string;
  addr_si: string | null;
  addr_gu: string | null;
  watt_upper: number | null;
  gov_unit_price: number | null;
  subsidy_gov: number | null;
  unit_price: number | null;
  subsidy_si: number | null;
  subsidy_gu: number | null;
  install_type: string | null;
  variant: string | null;
  program: string | null;
}

/** 보조금 미등록 자치구 (레거시 SubsidyGuList). */
export interface GuCandidate {
  id: number;
  addr_si_id: number;
  name: string;
}

export interface TerminalOut {
  id: number;
  def_terminal_type_id: number | null;
  build_company_id: number | null;
  name: string;
  model: string | null;
  maker: string | null;
  capacity: number | null;
  spec: string | null;
}

export interface TerminalTypeOut {
  id: number;
  name: string;
}

export interface TerminalListQuery {
  type_id?: number;
  keyword?: string;
  page?: number;
  size?: number;
}

export interface TerminalCreateBody {
  def_terminal_type_id: number;
  name: string;
  model: string;
  capacity?: number;
  /** 제조사/규격 — 2026 보급사업 공고의 모듈·인버터 사양 기재용(선택). */
  maker?: string | null;
  spec?: string | null;
}

export interface TerminalUpdateBody {
  def_terminal_type_id?: number;
  name?: string;
  model?: string;
  capacity?: number;
  maker?: string | null;
  spec?: string | null;
}

/** 상품 구성품. id 가 있으면 수정, 없으면 신규 (레거시 component_id 배열 파리티). */
export interface ProductCompIn {
  id?: number;
  def_terminal_id: number;
  qty: number;
}

export interface ProductCompOut {
  id: number;
  def_terminal_id: number | null;
  qty: number | null;
}

export interface ProductOut {
  id: number;
  build_company_id: number | null;
  name: string;
  price: number | null;
  components: ProductCompOut[];
}

export interface ProductListQuery {
  keyword?: string;
  page?: number;
  size?: number;
}

export interface ProductCreateBody {
  name: string;
  price?: number;
  components: ProductCompIn[];
}

export interface ProductUpdateBody {
  name?: string;
  price?: number;
  /** 전달 시 전체 동기화: 목록에 없는 기존 구성품은 삭제된다. */
  components?: ProductCompIn[];
}

export interface CapacityOut {
  id: number;
  capacity: number;
  year: string | null;
}

// --- 설치회사/팀 — app/schemas/company.py 와 1:1 ---------------------------
export interface CompanyOut {
  id: number;
  name: string;
  ceo: string | null;
  biz_no: string | null;
  tel: string | null;
  addr: string | null;
  old_addr: string | null;
  email: string | null;
  bank_name: string | null;
  account_number: string | null;
  account_name: string | null;
  company_state: number | null;
  as_state: number | null;
  etc: string | null;
  is_active: boolean;
}

export interface CompanyListQuery {
  addr?: string;
  name?: string;
  page?: number;
  size?: number;
}

export interface CompanyCreateBody {
  name: string;
  tel: string;
  addr: string;
  old_addr: string;
  email?: string;
  ceo?: string;
  biz_no?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  company_state?: number;
  as_state?: number;
  etc?: string;
}

export type CompanyUpdateBody = Partial<CompanyCreateBody>;

export interface TeamOut {
  id: number;
  build_company_id: number;
  name: string;
  memo: string | null;
  /** 소속 설치기사 id 목록 (레거시 TeamDetail 의 engineers). */
  engineer_ids: number[];
}

export interface TeamCreateBody {
  name: string;
  memo?: string;
  engineer_ids?: number[];
}

/** 전달 시 engineer_ids 는 전체 동기화 — 목록에서 빠진 기사는 팀에서 해제된다. */
export interface TeamUpdateBody {
  name?: string;
  memo?: string;
  engineer_ids?: number[];
}

// --- 게시판 — app/schemas/board.py 와 1:1 -----------------------------------
/** 게시판 5종 — app/core/enums.py BoardType (citizen-qna 만 URL 관례상 하이픈). */
export type BoardKind = 'notice' | 'data' | 'member' | 'faq' | 'citizen-qna';

/** bd_faq.type — 레거시 getFaqType 고정 목록 (helpers.php:385). */
export type FaqType = 'principle' | 'effect' | 'how' | 'manage' | 'safe';

export interface BoardListQuery {
  /** searchWord — 제목/내용 LIKE (자료실은 대표파일명 포함, 서버 소관). */
  keyword?: string;
  date_from?: string;
  date_to?: string;
  /** faq(bd_faq.type)·citizen-qna(citizen_user_qna.type) 분류 필터. */
  type?: string;
  /** citizen-qna 답변 여부 (레거시 searchResType 'Y'/'N'). */
  responded?: boolean;
  page?: number;
  size?: number;
}

export interface BoardListItem {
  id: number;
  title: string;
  users_id: number | null;
  author_name: string | null;
  /** data/member 만 — 회사 게시판 귀속. */
  build_company_id: number | null;
  /** notice 만 — 시민 포털 노출 분류. */
  category: string | null;
  /** faq/citizen-qna 분류. */
  type: string | null;
  /** notice/data/member 만. */
  view_cnt: number | null;
  /** citizen-qna 작성 시민 이름. */
  user_name: string | null;
  /** citizen-qna 답변 완료 시각. */
  responded_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BoardFileOut {
  id: number;
  file_name: string;
  /** 만료되는 presigned GET — 레거시 무인증 직접 서빙 대체. */
  url: string | null;
}

/** 직원게시판 댓글 (bd_member_comment). */
export interface BoardCommentOut {
  id: number;
  bd_member_id: number;
  users_id: number | null;
  author_name: string | null;
  contents: string;
  created_at: string | null;
}

export interface BoardDetailOut extends BoardListItem {
  contents: string | null;
  user_phone: string | null;
  citizen_user_id: number | null;
  res_content: string | null;
  res_admin_id: number | null;
  files: BoardFileOut[];
  /** member 만 채워진다. */
  comments: BoardCommentOut[];
}

/** `POST /boards/{type}` 본문 — BoardCreate 스키마와 1:1. 타입별 필수 검증은 서버(422). */
export interface BoardCreateBody {
  title: string;
  contents?: string;
  /** notice 전용. */
  category?: string;
  /** faq 전용 — 서버가 필수로 검증한다. */
  faq_type?: FaqType;
  /** citizen-qna 대리 등록 전용. */
  user_name?: string;
  user_phone?: string;
  qna_type?: string;
  /** citizen-qna 답변 — 전달 시 서버가 responded_at/res_admin_id 를 함께 갱신한다. */
  res_content?: string;
}

export type BoardUpdateBody = Partial<BoardCreateBody>;

// --- 재고 관리 — app/schemas/inventory.py 와 1:1 ----------------------------
/** build_inventory.type — add 입고 / minus 출고. */
export type InventoryEntryType = 'add' | 'minus';

/** 단말별 재고 요약 — stock = add_total - minus_total (서버 산식). */
export interface InventorySummaryOut {
  def_terminal_id: number;
  name: string;
  model: string | null;
  type_name: string | null;
  add_total: number;
  minus_total: number;
  stock: number;
}

export interface InventoryListQuery {
  /** 단말 종류 필터 (레거시 searchType). */
  terminal_type_id?: number;
  page?: number;
  size?: number;
}

export interface InventoryEntryQuery {
  entry_type?: InventoryEntryType;
  date_from?: string;
  date_to?: string;
  page?: number;
  size?: number;
}

export interface InventoryEntryOut {
  id: number;
  def_terminal_id: number;
  build_company_id: number | null;
  build_order_id: number | null;
  users_id: number | null;
  type: string;
  /** 'auto' = 주문 완료 처리 시 자동 출고 행. */
  status: string | null;
  count: number;
  created_at: string | null;
}

/** 이력 + 기간 합계 — 필터가 합계에도 그대로 걸린다(레거시 파리티, 서버 소관). */
export interface InventoryEntriesOut {
  items: InventoryEntryOut[];
  total: number;
  page: number;
  size: number;
  add_total: number;
  minus_total: number;
  stock: number;
}

export interface InventoryEntryCreateBody {
  def_terminal_id: number;
  entry_type: InventoryEntryType;
  count: number;
}

export type InventoryEntryUpdateBody = Partial<InventoryEntryCreateBody>;

// --- 통계 / 대시보드 — app/schemas/stats.py 와 1:1 --------------------------
/** 레거시 검색타입 (day: 일별 | month: 월별). */
export type StatPeriod = 'day' | 'month';

export interface StatsQuery {
  year?: number;
  month?: number;
  period?: StatPeriod;
  /** 드릴다운: 에너지공사(1,2)=회사 ID, 보급업체(3,5)=팀 ID. */
  group_id?: number;
}

/** 정산 통계는 그룹 드릴다운이 없다 (레거시 money 화면 파리티). */
export type SettlementStatsQuery = Omit<StatsQuery, 'group_id'>;

export interface StatPoint {
  date: string;
  total: number;
  completed: number;
}

export interface GroupStat {
  id: number;
  name: string;
  /** 보급업체(3,5)의 팀 목록에서만 채워진다 — 에너지공사 회사 목록은 null. */
  total_count: number | null;
  complete_count: number | null;
}

export interface OrderStatsOut {
  /** 상단 카드의 기준 연도 — 조회 연도와 무관하게 항상 올해(레거시 파리티). */
  year: number;
  total_count: number;
  complete_count: number;
  series: StatPoint[];
  groups: GroupStat[];
}

/** AS 통계 — 구조는 주문 통계와 동일, 완료 조건만 다르다(서버 소관). */
export type AsStatsOut = OrderStatsOut;

export interface SettlementPoint {
  date: string;
  total_price: number;
  total_payment: number;
  no_team_count: number;
}

export interface TeamAmount {
  team_id: number;
  name: string;
  amount: number;
}

export interface SettlementStatsOut {
  series: SettlementPoint[];
  teams: TeamAmount[];
}

export interface DashboardItem {
  key: string;
  label: string;
  count: number;
}

export interface DashboardOut {
  items: DashboardItem[];
}

// --- 엑셀 다운로드 ----------------------------------------------------------
/** `GET /excel/users` 쿼리 — app/schemas/user.py 의 UserExcelQuery 와 1:1. */
export interface UserExcelQuery {
  status?: string;
  company_id?: number;
  role?: number;
  keyword?: string;
}

/** 엑셀 다운로드 결과. 파일명은 서버 Content-Disposition 에서 읽는다(없으면 null). */
export interface FileDownload {
  blob: Blob;
  filename: string | null;
}

/** `GET /meta/codes` — 레거시는 이 값들을 PHP/JSP/Java/Dart 에 각각 중복 정의했다. */
export interface CodeTables {
  role: CodeEntry[];
  order_state: CodeEntry[];
  doc_state: CodeEntry[];
  gu_state: CodeEntry[];
  install_complete: CodeEntry[];
  apt_type: CodeEntry[];
  install_type: CodeEntry[];
  req_type: CodeEntry[];
  as_state: CodeEntry[];
}

export interface CodeEntry {
  value: number | string;
  name: string;
}

// --- 시민 온라인 신청 — app/schemas/citizen.py 와 1:1 (전부 무인증) ---------
/** 본인확인 3요소 — 조회/AS 는 세 값 완전 일치 건만 허용된다(서버 소관). */
export interface CitizenIdentity {
  user_name: string;
  /** YYYY.MM.DD 또는 YYYY-MM-DD — 형식 검증은 서버 422 메시지에 맡긴다. */
  user_birth: string;
  user_phone: string;
}

/** 신청 폼 보급업체 목록 — 민감정보(계좌/사업자번호)는 서버가 정의하지 않는다. */
export interface CitizenCompanyOut {
  id: number;
  name: string;
  tel: string | null;
  addr: string | null;
}

export interface CitizenProductComponent {
  name: string;
  capacity: number | null;
  qty: number | null;
}

/**
 * 상품 안내 목록 — 이름/가격/구성 + 2026 보급사업 용량/연도.
 * `company_id` 가 null 이면 공용 상품(인천 보급사업: 시가 규격을 정하고 업체는
 * 시공만 하므로 업체별 상품이 아니다).
 */
export interface CitizenProductOut {
  id: number;
  name: string;
  price: number | null;
  company_id: number | null;
  company_name: string | null;
  /** 모듈 총용량(W) — 사업 상한(서울 500W, 인천 445/890W) 판정과 견적의 기준. 서버는 모르면 0. */
  capacity_w: number;
  /** 보급사업 연도('2026') — 과년도 상품은 신청 폼에서 제외할 수 있게 내린다. */
  year: string | null;
  components: CitizenProductComponent[];
}

/** 설치 유형 — 난간형(rail) / 옥상형(rooftop, 인천 전용) / 난간형 외(other). */
export type CitizenInstallType = 'rail' | 'rooftop' | 'other';

/** 신청 구분 — null 일반 / group 단체(10가구 이상) / guard 경비실. 인천 보조율에만 영향. */
export type CitizenVariant = 'group' | 'guard';

/** `POST /api/v1/citizen/estimate` 본문 — 상품 + 시·도/구로 예상 사업비를 미리 계산한다. */
export interface CitizenEstimateBody {
  def_product_id: number;
  /** 시·도 이름 — addr_si.name 과 같은 표기('서울', '인천'). */
  addr1: string;
  /** 자치구/군 이름 — 기초지자체 보조율 조회 키. */
  addr2: string;
  install_type?: CitizenInstallType;
  variant?: CitizenVariant | null;
  program_year?: string;
}

/**
 * 예상 사업비 내역 — 총사업비/정부지원금/시·도 지원금/기초지자체 지원금/자부담금.
 * 지자체 확인 전 추정치라 화면에는 반드시 "확인 후 확정" 안내를 함께 둔다.
 */
export interface CitizenEstimate {
  price: number;
  price_gov: number;
  price_si: number;
  price_borough: number;
  payment: number;
}

/** `POST /api/v1/citizen/orders` 본문 — CitizenOrderCreate 스키마와 1:1. */
export interface CitizenOrderCreateBody extends CitizenIdentity {
  build_company_id: number;
  apt_type: number;
  /** 오늘 포함 미래만 — 과거 날짜는 서버가 422 로 거부한다. */
  install_date: string;
  /** 2026 보급사업 상품 선택 — 없으면 업체가 확인 후 지정한다(상품 미정). */
  def_product_id?: number;
  install_type?: CitizenInstallType;
  variant?: CitizenVariant | null;
  program_year?: string;
  user_email?: string;
  req_target?: 'normal' | 'basicNormal' | 'basicOne';
  req_type?: 'host' | 'tenant';
  new_addr?: string;
  addr_full?: string;
  post_code?: string;
  addr1?: string;
  addr2?: string;
  addr3?: string;
  addr4?: string;
  /** apt_type 5(단독주택)면 서버가 필수로 검증한다. */
  addr5?: string;
  building_name?: string;
  host?: string;
  /** 5자 이하 (레거시 HostRelationValidator 의도 시행). */
  host_relation?: string;
  host_install_agree?: 'Y' | 'N';
  etc?: string;
}

/** 신청 완료 응답 — receipt_no 로 진행 상황을 조회한다. */
export interface CitizenReceipt {
  receipt_no: number;
  message: string;
}

/** 설치 신청 완료 응답 — 상품을 골랐으면 서버가 확정한 예상 사업비를 함께 내린다. */
export interface CitizenOrderCreated extends CitizenReceipt {
  estimate: CitizenEstimate | null;
}

/**
 * 시민 계약서 작성 링크 — 서명 페이지(eform) URL. 토큰은 서버가 해시로만 보관하고
 * 만료가 있으므로 매번 새로 발급받는다. 이미 서명했으면 409 또는 already_signed.
 */
export interface CitizenContractLink {
  url: string;
  expires_at: string;
  already_signed: boolean;
}

/** `GET /api/v1/contracts/templates` 항목 — 관리자 서명 링크 발급 시 서식 선택용. */
export interface ContractTemplateSummary {
  code: string;
  version: number;
  /** 'seoul' | 'incheon' | 'daegu' | 'korea' … 지역 무관 서식은 null. */
  region: string | null;
  title: string;
}

/** 진행 상황 항목 — 주소는 서버가 마스킹해 내려준다(그대로 표시). */
export interface CitizenOrderStatus {
  receipt_no: number;
  state_type: number;
  install_complete: string | null;
  install_date: string | null;
  install_due_date: string | null;
  company_name: string | null;
  addr: string | null;
  applied_at: string | null;
  /** 온라인 계약 — 서명 완료 여부 / 작성 가능 여부(상품·금액이 확정돼야 true). */
  contract_signed: boolean;
  contract_available: boolean;
  /** 상품·금액 — 상품 미정이면 전부 null. 본인확인 일치 건에만 내려간다. */
  def_product_name: string | null;
  price: number | null;
  price_gov: number | null;
  price_si: number | null;
  price_borough: number | null;
  payment: number | null;
}

/** `POST /api/v1/citizen/as` 본문 — CitizenAsCreate 스키마와 1:1. */
export interface CitizenAsCreateBody extends CitizenIdentity {
  build_order_id: number;
  etc: string;
  /** A/S 희망일 (오늘 포함 미래) — ISO(YYYY-MM-DD). */
  as_date: string;
}

// --- 시민 공지/FAQ/1:1 문의 — app/schemas/citizen.py 와 1:1 (전부 무인증) ----
/** 공지 목록 항목 — 본문은 상세에서만 내려온다(레거시 목록도 제목만 표시). */
export interface CitizenNoticeItem {
  id: number;
  /** 레거시 CitizenNotice category — 표시용 자유 문자열. */
  category: string | null;
  title: string;
  view_cnt: number;
  created_at: string | null;
}

/** 공지 첨부 — 만료되는 presigned GET(레거시 무인증 직접 스트리밍 대체). */
export interface CitizenNoticeFile {
  id: number;
  file_name: string;
  url: string | null;
}

export interface CitizenNoticeDetail extends CitizenNoticeItem {
  contents: string | null;
  files: CitizenNoticeFile[];
}

/** FAQ 항목 — type 은 레거시 분류 5종(FaqType)이지만 자유 문자열로 받는다. */
export interface CitizenFaqItem {
  id: number;
  type: string | null;
  title: string;
  contents: string | null;
}

/** `POST /api/v1/citizen/inquiries` 본문 — 레거시 mtm/proc.do 파리티.
 * 본인확인 3요소를 함께 저장해 내 문의 조회(lookup)에 쓴다. */
export interface CitizenInquiryCreateBody extends CitizenIdentity {
  title: string;
  contents: string;
  /** 문의 분류(백오피스 필터용) — 선택. */
  qna_type?: string;
}

/** 내 문의 + 답변 — 본인확인 완전 일치 건만 내려온다. */
export interface CitizenInquiryItem {
  id: number;
  type: string | null;
  title: string | null;
  contents: string | null;
  created_at: string | null;
  res_content: string | null;
  responded_at: string | null;
}

export interface SignaturePoint {
  x: number;
  y: number;
  t: number;
}

export interface ContractView {
  template: {
    code: string;
    version: number;
    region: string | null;
    title: string;
    pages: TemplatePage[];
    signatures?: Array<{ slot: string; label: string; required: boolean }>;
  };
  data: Record<string, unknown>;
  expires_at: string;
  already_signed: boolean;
  /** 서명 완료 화면이 돌아갈 시민 포털 '진행 조회' 주소. 포털이 없는 배포는 null. */
  citizen_status_url?: string | null;
}

export interface TemplatePage {
  id: string;
  title?: string;
  showIf?: string;
  blocks: TemplateBlock[];
}

export type TemplateBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'fields'; fields: Array<{ label: string; path: string; format?: 'won' }> }
  | {
      type: 'table';
      path: string;
      /** width: CSS 길이("40%"). 설비 사양표의 '규격'처럼 긴 열에 폭을 몰아준다. 없으면 자동. */
      columns: Array<{ key: string; label: string; width?: string }>;
    }
  | {
      type: 'signature';
      slot: string;
      label?: string;
      propagate?: string[];
      /** false 면 서명 없이도 제출할 수 있다 — 2026 서식의 시공업체 직인(시민 단독 서명). */
      required?: boolean;
    };

// --------------------------------------------------------------------- 2018 전자계약서
/** 2018년도 전자계약서 아카이브 — 레거시 order/pdf_2018 (미리 생성된 PDF 목록). */
export interface Pdf2018Query {
  /** 자치구. 없으면 서버가 목록 첫 번째를 쓴다. */
  gu?: string;
  /** 파일명 부분 일치 (대소문자 무시) */
  q?: string;
  page?: number;
  size?: number;
}

export interface Pdf2018FileOut {
  name: string;
  size: number;
  last_modified: string | null;
  /** presigned GET — 짧게 만료되므로 목록을 다시 읽으면 새로 받는다. */
  download_url: string;
}

// --- 계정 감사 이력 (app/schemas/history.py) ----------------------------------
/** `GET /auth/history` 쿼리 — 레거시 UserHistory 필터. company_id 는 역할 1,2 만 의미 있다. */
export interface UserHistoryQuery {
  company_id?: number;
  type?: 'login' | 'excel';
  keyword?: string;
  /** YYYY-MM-DD. 종료일은 당일까지 포함(서버가 +1일 미만으로 건다). */
  date_from?: string;
  date_to?: string;
  page?: number;
  size?: number;
}

export interface UserHistoryItem {
  id: number;
  users_id: number;
  user_id: string | null;
  name: string | null;
  build_company_id: number | null;
  company_name: string | null;
  type: string;
  ip: string | null;
  /** 어떤 엑셀인지(orders/settlement/users/history) — 레거시에 없던 구분. */
  note: string | null;
  created_at: string | null;
}

/** 비밀번호 변경 조회 행 — id 는 users.id (일괄 처리 user_ids 로 그대로 보낸다). */
export interface PwChangeUserItem {
  id: number;
  user_id: string;
  name: string | null;
  type: number;
  status: string | null;
  build_company_id: number | null;
  company_name: string | null;
  /** MAX(pw_change_users.created_at) — 한 번도 안 바꿨으면 null. */
  changed_at: string | null;
}

export interface BulkResult {
  count: number;
}

export interface RoleHistoryItem {
  id: number;
  users_id: number;
  user_id: string | null;
  name: string | null;
  changer_id: number | null;
  changer_user_id: string | null;
  changer_name: string | null;
  pre_role: number | null;
  be_role: number;
  created_at: string | null;
}

// --------------------------------------------------------------------- 클라이언트
// --- 권한/역할 (계정 관리 > Permission / Role) ---------------------------------
/** 역할 참조 — 권한 목록의 '보유 역할' 열. */
export interface RoleRef {
  id: number;
  name: string;
}

export interface RoleOut extends RoleRef {
  depth: number;
  permission_codes: string[];
  /** 이 역할(users.type)을 가진 계정 수 — 0 이어야 삭제할 수 있다. */
  user_count: number;
  /** 1~9 시드 역할 — 삭제 불가. */
  seeded: boolean;
}

/** POST /auth/roles · PUT /auth/roles/{id} 공용 본문 (PUT 은 전체 교체). */
export interface RoleSaveBody {
  name: string;
  depth: number;
  permission_codes: string[];
}

/** 권한은 서버 코드로 고정된 읽기 전용 목록이다 (등록/삭제 없음). */
export interface PermissionOut {
  code: string;
  group_label: string;
  label: string;
  roles: RoleRef[];
}

export interface PermissionTreeItem {
  code: string;
  label: string;
}

/** 역할 편집 화면의 체크박스 격자 — 레거시 getPermissionMenu 의 19 그룹. */
export interface PermissionTreeGroup {
  key: string;
  label: string;
  items: PermissionTreeItem[];
}

export class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<string | null> | null = null;

  constructor(private config: ApiConfig) {}

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  private url(path: string): string {
    return `${this.config.baseUrl.replace(/\/$/, '')}${path}`;
  }

  private async raw(path: string, init: RequestInit, auth: boolean): Promise<Response> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (auth && this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }
    return fetch(this.url(path), { ...init, headers });
  }

  /** 요청 + 401 재발급 재시도 한 번. JSON/blob 응답이 공유하는 공통 경로. */
  private async send(path: string, init: RequestInit, auth: boolean): Promise<Response> {
    let response = await this.raw(path, init, auth);

    // access 토큰 만료 시 한 번만 재발급을 시도한다.
    if (response.status === 401 && auth) {
      const token = await this.refreshOnce();
      if (!token) {
        this.config.onUnauthorized?.();
        throw await toError(response);
      }
      response = await this.raw(path, init, auth);
    }

    if (!response.ok) throw await toError(response);
    return response;
  }

  private async request<T>(path: string, init: RequestInit = {}, auth = true): Promise<T> {
    const response = await this.send(path, init, auth);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  /** 엑셀 등 파일 응답 — 본문은 blob, 파일명은 Content-Disposition 에서 읽는다. */
  private async download(path: string): Promise<FileDownload> {
    const response = await this.send(path, {}, true);
    return {
      blob: await response.blob(),
      filename: filenameFromDisposition(response.headers.get('content-disposition')),
    };
  }

  /** 동시 요청이 각자 refresh 를 호출하면 회전 정책 때문에 서로를 무효화한다. */
  private refreshOnce(): Promise<string | null> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<string | null> {
    const refreshToken = await getToken();
    if (!refreshToken) return null;

    const response = await this.raw(
      '/api/v1/auth/refresh',
      { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) },
      false,
    );
    if (!response.ok) {
      await clearToken();
      return null;
    }

    const pair = (await response.json()) as TokenPair;
    this.accessToken = pair.access_token;
    await setToken(pair.refresh_token);
    return pair.access_token;
  }

  // --- auth ---------------------------------------------------------------
  async login(userId: string, password: string): Promise<TokenPair> {
    const pair = await this.request<TokenPair>(
      '/api/v1/auth/login',
      { method: 'POST', body: JSON.stringify({ user_id: userId, password }) },
      false,
    );
    this.accessToken = pair.access_token;
    await setToken(pair.refresh_token);
    return pair;
  }

  async logout(): Promise<void> {
    const refreshToken = await getToken();
    if (refreshToken) {
      await this.request<void>('/api/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => undefined);
    }
    this.accessToken = null;
    await clearToken();
  }

  me(): Promise<UserProfile> {
    return this.request<UserProfile>('/api/v1/auth/me');
  }

  /** 앱 재시작 시 저장된 refresh 토큰으로 세션을 복구한다. */
  async restoreSession(): Promise<UserProfile | null> {
    const token = await this.refreshOnce();
    if (!token) return null;
    return this.me().catch(() => null);
  }

  // --- meta ---------------------------------------------------------------
  codes(): Promise<CodeTables> {
    return this.request<CodeTables>('/api/v1/codes', {}, false);
  }

  // --- orders -------------------------------------------------------------
  listOrders(params: OrderListQuery): Promise<Page<OrderListItem>> {
    return this.request<Page<OrderListItem>>(`/api/v1/orders?${query(params)}`);
  }

  getOrder(id: number): Promise<OrderDetail> {
    return this.request<OrderDetail>(`/api/v1/orders/${id}`);
  }

  createOrder(body: OrderCreate): Promise<OrderDetail> {
    return this.request<OrderDetail>('/api/v1/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  listUninstallOrders(params: UninstallListQuery): Promise<Page<OrderListItem>> {
    return this.request<Page<OrderListItem>>(`/api/v1/orders/uninstall?${query(params)}`);
  }

  requestUninstall(id: number, body: UninstallRequest = {}): Promise<OrderDetail> {
    return this.request<OrderDetail>(`/api/v1/orders/${id}/uninstall`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // --- 일괄 처리 (전체 성공 아니면 전체 실패) -------------------------------
  batchEngineer(body: { ids: number[]; team_id: number; engineer_id: number }): Promise<BatchResult> {
    return this.request<BatchResult>('/api/v1/orders/batch/engineer', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** to: DocState — 2 제출 / 3 접수완료 / 4 보류(reason 첨부 가능). */
  batchDocState(body: { ids: number[]; to: number; reason?: string }): Promise<BatchResult> {
    return this.request<BatchResult>('/api/v1/orders/batch/doc-state', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** to: GuState — 1 대기(시 취소) / 2 지급(시)·구 취소 / 3 지급(구). */
  batchGuPrice(body: { ids: number[]; to: number; gu_date?: string }): Promise<BatchResult> {
    return this.request<BatchResult>('/api/v1/orders/batch/gu-price', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateOrder(id: number, patch: OrderUpdate, draft = false): Promise<OrderDetail> {
    return this.request<OrderDetail>(`/api/v1/orders/${id}?draft=${draft}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  cancelOrder(id: number, reason: string): Promise<OrderDetail> {
    return this.request<OrderDetail>(`/api/v1/orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * presign → S3 직접 PUT → 서버 확정, 3단계.
   *
   * 레거시는 사진을 base64 로 요청 본문에 실어 보냈다(용량 33% 증가, API 서버가
   * 업로드 대역폭을 그대로 부담). 여기서는 업로드 트래픽이 API 서버를 통과하지 않는다.
   * 마지막 확정 호출이 없으면 서버는 업로드 사실을 알 수 없다.
   */
  async uploadPhoto(
    orderId: number,
    slot: number,
    dataUrl: string,
    meta: { takenAt?: string; lat?: number; lng?: number } = {},
  ): Promise<PhotoOut> {
    const blob = await dataUrlToBlob(dataUrl);
    const presign = await this.request<{ url: string; key: string }>(
      `/api/v1/orders/${orderId}/photos/presign?slot=${slot}&content_type=${encodeURIComponent(blob.type)}`,
      { method: 'POST' },
    );

    const put = await fetch(presign.url, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': blob.type },
    });
    if (!put.ok) throw new ApiError(put.status, 'UploadFailed', '사진 업로드에 실패했습니다.');

    return this.request<PhotoOut>(`/api/v1/orders/${orderId}/photos`, {
      method: 'POST',
      body: JSON.stringify({
        slot,
        storage_key: presign.key,
        content_type: blob.type,
        byte_size: blob.size,
        taken_at: meta.takenAt,
        lat: meta.lat,
        lng: meta.lng,
      }),
    });
  }

  contractPdf(orderId: number): Promise<{ url: string | null; sha256?: string }> {
    return this.request(`/api/v1/orders/${orderId}/contract/pdf`);
  }

  // --- contract -----------------------------------------------------------
  /** template_code 를 비우면 서버가 주문 지역(서울/대구/인천)으로 서식을 고른다. */
  createInvite(
    orderId: number,
    body: { template_code?: string; phone?: string; send_sms?: boolean; message?: string },
  ): Promise<{ url: string; expires_at: string; sms_sent: boolean }> {
    return this.request(`/api/v1/orders/${orderId}/contract/invite`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** 공개 엔드포인트 — 토큰만으로 접근하므로 Authorization 을 붙이지 않는다. */
  viewContract(token: string): Promise<ContractView> {
    return this.request<ContractView>(`/api/v1/contracts/${token}`, {}, false);
  }

  signContract(
    token: string,
    body: {
      signatures: Array<{ slot: string; image: string; strokes: SignaturePoint[][] }>;
      agreements: Record<string, boolean>;
    },
  ): Promise<{ id: number; pdf_name: string; sha256: string }> {
    return this.request(
      `/api/v1/contracts/${token}/sign`,
      { method: 'POST', body: JSON.stringify(body) },
      false,
    );
  }

  // --- users --------------------------------------------------------------
  listUsers(params: UserListQuery): Promise<Page<UserListItem>> {
    return this.request<Page<UserListItem>>(`/api/v1/users?${query(params)}`);
  }

  getUser(id: number): Promise<UserDetail> {
    return this.request<UserDetail>(`/api/v1/users/${id}`);
  }

  createUser(body: UserCreateBody): Promise<UserDetail> {
    return this.request<UserDetail>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateUser(id: number, patch: UserUpdateBody): Promise<UserDetail> {
    return this.request<UserDetail>(`/api/v1/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  changePassword(id: number, password: string): Promise<UserDetail> {
    return this.request<UserDetail>(`/api/v1/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  }

  // --- 정보 관리(마스터) ----------------------------------------------------
  /** 시/도 목록 — [시]보조금 등록 셀렉트 (조회는 로그인 사용자 전체). */
  listAddrSi(): Promise<AddrSiOut[]> {
    return this.request<AddrSiOut[]>('/api/v1/define/addr-si');
  }

  /** 자치구 목록 — 구 관리자 자치구 지정 등 셀렉트. */
  listAddrGu(siId: number): Promise<AddrGuOut[]> {
    return this.request<AddrGuOut[]>(`/api/v1/define/addr-gu?si_id=${siId}`);
  }

  listSubsidies(params: SubsidyListQuery): Promise<Page<SubsidyOut>> {
    return this.request<Page<SubsidyOut>>(`/api/v1/define/subsidies?${query(params)}`);
  }

  /** 연도별 공고 단가표 — 읽기 전용 (관리자 보조금 화면 하단 참고 표). */
  listSubsidyRates(year = '2026'): Promise<SubsidyRateOut[]> {
    return this.request<SubsidyRateOut[]>(
      `/api/v1/define/subsidy-rates?year=${encodeURIComponent(year)}`,
    );
  }

  /** 보조금 미등록 자치구 — [구]보조금 등록 화면의 구 선택지 (레거시 SubsidyGuList). */
  guCandidates(addrSiId: number): Promise<GuCandidate[]> {
    return this.request<GuCandidate[]>(`/api/v1/define/subsidies/gu-candidates?addr_si_id=${addrSiId}`);
  }

  createSubsidy(body: SubsidyCreateBody): Promise<SubsidyOut> {
    return this.request<SubsidyOut>('/api/v1/define/subsidies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateSubsidy(id: number, patch: SubsidyUpdateBody): Promise<SubsidyOut> {
    return this.request<SubsidyOut>(`/api/v1/define/subsidies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteSubsidy(id: number): Promise<void> {
    return this.request<void>(`/api/v1/define/subsidies/${id}`, { method: 'DELETE' });
  }

  listTerminals(params: TerminalListQuery): Promise<Page<TerminalOut>> {
    return this.request<Page<TerminalOut>>(`/api/v1/define/terminals?${query(params)}`);
  }

  listTerminalTypes(): Promise<TerminalTypeOut[]> {
    return this.request<TerminalTypeOut[]>('/api/v1/define/terminal-types');
  }

  createTerminal(body: TerminalCreateBody): Promise<TerminalOut> {
    return this.request<TerminalOut>('/api/v1/define/terminals', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateTerminal(id: number, patch: TerminalUpdateBody): Promise<TerminalOut> {
    return this.request<TerminalOut>(`/api/v1/define/terminals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteTerminal(id: number): Promise<void> {
    return this.request<void>(`/api/v1/define/terminals/${id}`, { method: 'DELETE' });
  }

  listProducts(params: ProductListQuery): Promise<Page<ProductOut>> {
    return this.request<Page<ProductOut>>(`/api/v1/define/products?${query(params)}`);
  }

  getProduct(id: number): Promise<ProductOut> {
    return this.request<ProductOut>(`/api/v1/define/products/${id}`);
  }

  createProduct(body: ProductCreateBody): Promise<ProductOut> {
    return this.request<ProductOut>('/api/v1/define/products', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateProduct(id: number, patch: ProductUpdateBody): Promise<ProductOut> {
    return this.request<ProductOut>(`/api/v1/define/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteProduct(id: number): Promise<void> {
    return this.request<void>(`/api/v1/define/products/${id}`, { method: 'DELETE' });
  }

  /** 지원 가능 용량 — 단일값. 아직 등록 전이면 서버가 null 을 내려준다. */
  getCapacity(): Promise<CapacityOut | null> {
    return this.request<CapacityOut | null>('/api/v1/define/capacities');
  }

  setCapacity(capacity: number): Promise<CapacityOut> {
    return this.request<CapacityOut>('/api/v1/define/capacities', {
      method: 'POST',
      body: JSON.stringify({ capacity }),
    });
  }

  // --- 설치회사/팀 ----------------------------------------------------------
  listCompanies(params: CompanyListQuery = {}): Promise<Page<CompanyOut>> {
    return this.request<Page<CompanyOut>>(`/api/v1/companies?${query(params)}`);
  }

  getCompany(id: number): Promise<CompanyOut> {
    return this.request<CompanyOut>(`/api/v1/companies/${id}`);
  }

  createCompany(body: CompanyCreateBody): Promise<CompanyOut> {
    return this.request<CompanyOut>('/api/v1/companies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateCompany(id: number, patch: CompanyUpdateBody): Promise<CompanyOut> {
    return this.request<CompanyOut>(`/api/v1/companies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteCompany(id: number): Promise<void> {
    return this.request<void>(`/api/v1/companies/${id}`, { method: 'DELETE' });
  }

  /** 팀 목록 — 페이지네이션 없는 배열 (회사 하위 리소스). */
  listTeams(companyId: number): Promise<TeamOut[]> {
    return this.request<TeamOut[]>(`/api/v1/companies/${companyId}/teams`);
  }

  createTeam(companyId: number, body: TeamCreateBody): Promise<TeamOut> {
    return this.request<TeamOut>(`/api/v1/companies/${companyId}/teams`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateTeam(companyId: number, teamId: number, patch: TeamUpdateBody): Promise<TeamOut> {
    return this.request<TeamOut>(`/api/v1/companies/${companyId}/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteTeam(companyId: number, teamId: number): Promise<void> {
    return this.request<void>(`/api/v1/companies/${companyId}/teams/${teamId}`, {
      method: 'DELETE',
    });
  }

  // --- 게시판 ---------------------------------------------------------------
  listBoards(kind: BoardKind, params: BoardListQuery = {}): Promise<Page<BoardListItem>> {
    return this.request<Page<BoardListItem>>(`/api/v1/boards/${kind}?${query(params)}`);
  }

  /** 상세 — 서버가 진입 시 조회수를 올린다(notice/data/member, 레거시 파리티). */
  getBoard(kind: BoardKind, id: number): Promise<BoardDetailOut> {
    return this.request<BoardDetailOut>(`/api/v1/boards/${kind}/${id}`);
  }

  createBoard(kind: BoardKind, body: BoardCreateBody): Promise<BoardDetailOut> {
    return this.request<BoardDetailOut>(`/api/v1/boards/${kind}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateBoard(kind: BoardKind, id: number, patch: BoardUpdateBody): Promise<BoardDetailOut> {
    return this.request<BoardDetailOut>(`/api/v1/boards/${kind}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  /** citizen-qna 는 서버가 403 으로 거부한다(레거시에 삭제 라우트 없음). */
  deleteBoard(kind: BoardKind, id: number): Promise<void> {
    return this.request<void>(`/api/v1/boards/${kind}/${id}`, { method: 'DELETE' });
  }

  /**
   * 첨부 업로드 — presign → S3 직접 PUT → 서버 등록, 주문 사진과 동일한 2단계.
   * notice/data/member 만 지원한다(faq/citizen-qna 는 서버가 422 로 거부).
   */
  async uploadBoardFile(kind: BoardKind, itemId: number, file: File): Promise<BoardFileOut> {
    const contentType = file.type || 'application/octet-stream';
    const presign = await this.request<{ url: string; key: string }>(
      `/api/v1/boards/${kind}/${itemId}/files/presign?filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(contentType)}`,
      { method: 'POST' },
    );

    const put = await fetch(presign.url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!put.ok) throw new ApiError(put.status, 'UploadFailed', '첨부파일 업로드에 실패했습니다.');

    return this.request<BoardFileOut>(`/api/v1/boards/${kind}/${itemId}/files`, {
      method: 'POST',
      body: JSON.stringify({ file_name: file.name, storage_key: presign.key }),
    });
  }

  deleteBoardFile(kind: BoardKind, itemId: number, fileId: number): Promise<void> {
    return this.request<void>(`/api/v1/boards/${kind}/${itemId}/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  /** 직원게시판 댓글 — 부모 글 접근 가능 = 댓글 가능(서버 소관). */
  addBoardComment(itemId: number, contents: string): Promise<BoardCommentOut> {
    return this.request<BoardCommentOut>(`/api/v1/boards/member/${itemId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ contents }),
    });
  }

  deleteBoardComment(itemId: number, commentId: number): Promise<void> {
    return this.request<void>(`/api/v1/boards/member/${itemId}/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // --- 재고 관리 -------------------------------------------------------------
  /** 단말별 재고 요약 — 회사 격리는 서버가 강제한다(에너지공사는 전체). */
  listInventory(params: InventoryListQuery = {}): Promise<Page<InventorySummaryOut>> {
    return this.request<Page<InventorySummaryOut>>(`/api/v1/inventory?${query(params)}`);
  }

  /** 단말 하나의 입출고 이력 + 합계 (레거시 InventoryDetail 파리티). */
  listInventoryEntries(
    terminalId: number,
    params: InventoryEntryQuery = {},
  ): Promise<InventoryEntriesOut> {
    return this.request<InventoryEntriesOut>(
      `/api/v1/inventory/${terminalId}/entries?${query(params)}`,
    );
  }

  createInventoryEntry(body: InventoryEntryCreateBody): Promise<InventoryEntryOut> {
    return this.request<InventoryEntryOut>('/api/v1/inventory', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateInventoryEntry(id: number, patch: InventoryEntryUpdateBody): Promise<InventoryEntryOut> {
    return this.request<InventoryEntryOut>(`/api/v1/inventory/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  // --- as -----------------------------------------------------------------
  listAs(params: AsListQuery): Promise<Page<AsListItem>> {
    return this.request<Page<AsListItem>>(`/api/v1/as?${query(params)}`);
  }

  getAs(id: number): Promise<AsDetail> {
    return this.request<AsDetail>(`/api/v1/as/${id}`);
  }

  createAs(body: { build_order_id: number; etc: string; as_date?: string }): Promise<AsDetail> {
    return this.request<AsDetail>('/api/v1/as', { method: 'POST', body: JSON.stringify(body) });
  }

  assignAs(
    id: number,
    body: { engineer_id: number; team_id?: number; as_due_date?: string },
  ): Promise<AsDetail> {
    return this.request<AsDetail>(`/api/v1/as/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  completeAs(id: number, memo: string, comDate?: string): Promise<AsDetail> {
    return this.request<AsDetail>(`/api/v1/as/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ engineer_memo: memo, com_date: comDate }),
    });
  }

  // --- 시민 온라인 신청 (전부 무인증 — Authorization 을 붙이지 않는다) --------
  citizenCompanies(): Promise<CitizenCompanyOut[]> {
    return this.request<CitizenCompanyOut[]>('/api/v1/citizen/companies', {}, false);
  }

  /** company_id 를 주면 그 업체 상품 + 공용 상품(company_id null)만, 없으면 전체. */
  citizenProducts(params: { company_id?: number } = {}): Promise<CitizenProductOut[]> {
    const qs = query(params);
    return this.request<CitizenProductOut[]>(
      `/api/v1/citizen/products${qs ? `?${qs}` : ''}`,
      {},
      false,
    );
  }

  createCitizenOrder(body: CitizenOrderCreateBody): Promise<CitizenOrderCreated> {
    return this.request<CitizenOrderCreated>(
      '/api/v1/citizen/orders',
      { method: 'POST', body: JSON.stringify(body) },
      false,
    );
  }

  /**
   * 예상 사업비 — 상품 용량 × 지역 단가/보조율. 용량이 사업 상한을 넘거나 단가가
   * 없으면 422 + 한국어 메시지가 오므로 호출측은 ApiError.message 를 그대로 보여준다.
   */
  citizenEstimate(body: CitizenEstimateBody): Promise<CitizenEstimate> {
    return this.request<CitizenEstimate>(
      '/api/v1/citizen/estimate',
      { method: 'POST', body: JSON.stringify(body) },
      false,
    );
  }

  /**
   * 시민 계약서 작성 링크 — 본인확인 3요소가 접수 당시와 일치해야 발급된다.
   * 이미 서명한 주문은 409 (호출측은 "서명 완료" 로 갱신).
   */
  citizenContractLink(receiptNo: number, identity: CitizenIdentity): Promise<CitizenContractLink> {
    return this.request<CitizenContractLink>(
      `/api/v1/citizen/orders/${receiptNo}/contract`,
      { method: 'POST', body: JSON.stringify(identity) },
      false,
    );
  }

  /** 본인확인 실패 시에도 200 + 빈 목록 — 존재 여부는 응답으로 구분되지 않는다. */
  async lookupCitizenOrders(identity: CitizenIdentity): Promise<CitizenOrderStatus[]> {
    const response = await this.request<{ items: CitizenOrderStatus[] }>(
      '/api/v1/citizen/orders/lookup',
      { method: 'POST', body: JSON.stringify(identity) },
      false,
    );
    return response.items;
  }

  createCitizenAs(body: CitizenAsCreateBody): Promise<CitizenReceipt> {
    return this.request<CitizenReceipt>(
      '/api/v1/citizen/as',
      { method: 'POST', body: JSON.stringify(body) },
      false,
    );
  }

  /** 공지 목록 — 레거시 citizen/notice.do (미삭제 전건, 최신순). */
  citizenNotices(): Promise<CitizenNoticeItem[]> {
    return this.request<CitizenNoticeItem[]>('/api/v1/citizen/notices', {}, false);
  }

  /** 공지 상세 — 서버가 진입 시 조회수를 올린다(레거시 noticeDetail 파리티). */
  citizenNotice(id: number): Promise<CitizenNoticeDetail> {
    return this.request<CitizenNoticeDetail>(`/api/v1/citizen/notices/${id}`, {}, false);
  }

  /** FAQ 목록 — 분류 묶음은 클라이언트가 한다(레거시 JSP 탭 5종 대응). */
  citizenFaqs(): Promise<CitizenFaqItem[]> {
    return this.request<CitizenFaqItem[]>('/api/v1/citizen/faqs', {}, false);
  }

  createCitizenInquiry(body: CitizenInquiryCreateBody): Promise<CitizenReceipt> {
    return this.request<CitizenReceipt>(
      '/api/v1/citizen/inquiries',
      { method: 'POST', body: JSON.stringify(body) },
      false,
    );
  }

  /** 내 문의 조회 — 본인확인 실패 시에도 200 + 빈 목록(존재 여부 비노출). */
  async lookupCitizenInquiries(identity: CitizenIdentity): Promise<CitizenInquiryItem[]> {
    const response = await this.request<{ items: CitizenInquiryItem[] }>(
      '/api/v1/citizen/inquiries/lookup',
      { method: 'POST', body: JSON.stringify(identity) },
      false,
    );
    return response.items;
  }

  // --- 통계 / 대시보드 ------------------------------------------------------
  /** 역할별 대시보드 카드 (전 역할). */
  dashboard(): Promise<DashboardOut> {
    return this.request<DashboardOut>('/api/v1/dashboard');
  }

  /** 주문 통계 — 서버가 역할 1,2,3,5 외에는 403 을 돌려준다. */
  orderStats(params: StatsQuery = {}): Promise<OrderStatsOut> {
    return this.request<OrderStatsOut>(`/api/v1/stats/orders?${query(params)}`);
  }

  /** 정산 통계 — 역할 1,2,3,5 전용. */
  settlementStats(params: SettlementStatsQuery = {}): Promise<SettlementStatsOut> {
    return this.request<SettlementStatsOut>(`/api/v1/stats/settlement?${query(params)}`);
  }

  /** AS 통계 — 역할 1,2,3,5 전용. */
  asStats(params: StatsQuery = {}): Promise<AsStatsOut> {
    return this.request<AsStatsOut>(`/api/v1/stats/as?${query(params)}`);
  }

  // --- 엑셀 다운로드 --------------------------------------------------------
  /** 주문 엑셀 — 목록과 동일한 필터를 그대로 받는다. */
  ordersExcel(params: OrderListQuery = {}): Promise<FileDownload> {
    return this.download(`/api/v1/excel/orders?${query(params)}`);
  }

  /** 지급내역서(정산) 엑셀 — 서버가 역할 1,2,6 외에는 403 을 돌려준다. */
  settlementExcel(params: OrderListQuery = {}): Promise<FileDownload> {
    return this.download(`/api/v1/excel/settlement?${query(params)}`);
  }

  /** 사용자 엑셀 — 서버가 역할 1,2,3,5 외에는 403 을 돌려준다. */
  usersExcel(params: UserExcelQuery = {}): Promise<FileDownload> {
    return this.download(`/api/v1/excel/users?${query(params)}`);
  }

  // --- 2018 전자계약서 아카이브 ------------------------------------------------
  /** 자치구 목록 — 레거시 Storage::directories('public/pdf_2018') 파리티. */
  listPdf2018Districts(): Promise<{ districts: string[] }> {
    return this.request<{ districts: string[] }>('/api/v1/orders/pdf-2018/districts');
  }

  /** 자치구 하나의 PDF 목록 — 서버가 역할 4,8 에는 403, 없는 자치구에는 404 를 준다. */
  listPdf2018(params: Pdf2018Query = {}): Promise<Page<Pdf2018FileOut>> {
    return this.request<Page<Pdf2018FileOut>>(`/api/v1/orders/pdf-2018?${query(params)}`);
  }

  // --- 계정 감사 이력 ----------------------------------------------------------
  /** 사용자 작업 이력 — 역할 1,2 전체 / 3 은 자기 회사만, 그 외 403. */
  listUserHistory(params: UserHistoryQuery = {}): Promise<Page<UserHistoryItem>> {
    return this.request<Page<UserHistoryItem>>(`/api/v1/auth/history?${query(params)}`);
  }

  /** 이력 엑셀 — 역할 1 만 (서버 403). 다운로드 자체도 이력에 남는다. */
  userHistoryExcel(params: UserHistoryQuery = {}): Promise<FileDownload> {
    return this.download(`/api/v1/auth/history/excel?${query(params)}`);
  }

  listPwChangeUsers(params: { page?: number; size?: number } = {}): Promise<Page<PwChangeUserItem>> {
    return this.request<Page<PwChangeUserItem>>(`/api/v1/auth/pw-change-users?${query(params)}`);
  }

  /** 비밀번호 일괄변경 — 한 명이라도 직전 비밀번호 재사용이면 전체 422. */
  bulkChangePassword(userIds: number[], password: string): Promise<BulkResult> {
    return this.request<BulkResult>('/api/v1/auth/pw-change-users/change-password', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, password }),
    });
  }

  bulkDeleteUsers(userIds: number[]): Promise<BulkResult> {
    return this.request<BulkResult>('/api/v1/auth/pw-change-users/delete', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds }),
    });
  }

  bulkStopUsers(userIds: number[]): Promise<BulkResult> {
    return this.request<BulkResult>('/api/v1/auth/pw-change-users/stop', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds }),
    });
  }

  listRoleHistory(params: { page?: number; size?: number } = {}): Promise<Page<RoleHistoryItem>> {
    return this.request<Page<RoleHistoryItem>>(`/api/v1/auth/role-history?${query(params)}`);
  }

  // --- 권한/역할 -------------------------------------------------------------
  /** 권한 목록 — 코드 정의 순서, 각 코드를 가진(열람 가능한) 역할 포함. */
  listPermissions(): Promise<PermissionOut[]> {
    return this.request<PermissionOut[]>('/api/v1/auth/permissions');
  }

  permissionTree(): Promise<PermissionTreeGroup[]> {
    return this.request<PermissionTreeGroup[]>('/api/v1/auth/permission-tree');
  }

  /** 역할 목록 — 서버가 `depth >= 열람자 depth` 로 걸러서 내려준다. */
  listRoles(): Promise<RoleOut[]> {
    return this.request<RoleOut[]>('/api/v1/auth/roles');
  }

  getRole(id: number): Promise<RoleOut> {
    return this.request<RoleOut>(`/api/v1/auth/roles/${id}`);
  }

  /** id 가 없으면 사용자 정의 역할 등록(POST), 있으면 전체 교체(PUT). */
  saveRole(id: number | null, body: RoleSaveBody): Promise<RoleOut> {
    if (id === null) {
      return this.request<RoleOut>('/api/v1/auth/roles', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    }
    return this.request<RoleOut>(`/api/v1/auth/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  /** 시드 역할(1~9)·사용자가 붙은 역할은 서버가 409 로 거부한다. */
  deleteRole(id: number): Promise<void> {
    return this.request<void>(`/api/v1/auth/roles/${id}`, { method: 'DELETE' });
  }

  // --- 계약 서식 ------------------------------------------------------------
  /** 활성 서식 목록 — 관리자 서명 링크 발급 화면의 서식 선택지(지역 자동 선택이 기본). */
  listContractTemplates(): Promise<ContractTemplateSummary[]> {
    return this.request<ContractTemplateSummary[]>('/api/v1/contracts/templates');
  }
}

// --------------------------------------------------------------------- 유틸
function query(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

async function toError(response: Response): Promise<ApiError> {
  let detail = '요청을 처리하지 못했습니다.';
  let code = 'Unknown';
  try {
    const body = (await response.json()) as { detail?: unknown; code?: string };
    if (typeof body.detail === 'string') {
      detail = body.detail;
    } else if (Array.isArray(body.detail)) {
      // FastAPI 422 검증 오류는 [{loc, msg, ...}] 배열이다. 필드명: 메시지로
      // 합쳐서 서버 메시지를 그대로 보여준다.
      const lines = body.detail
        .map((item) => {
          const { loc, msg } = item as { loc?: unknown[]; msg?: string };
          const field = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : '';
          return field && msg ? `${field}: ${msg}` : (msg ?? '');
        })
        .filter(Boolean);
      if (lines.length > 0) detail = lines.join('\n');
    }
    code = body.code ?? code;
  } catch {
    // 본문이 JSON 이 아닌 경우 (프록시 오류 등)
  }
  return new ApiError(response.status, code, detail);
}

/**
 * Content-Disposition 에서 파일명을 뽑는다.
 *
 * 서버는 한글 파일명을 RFC 5987 `filename*=UTF-8''…` 로 내려준다(excel.py).
 * 구형 `filename="…"` 도 함께 지원해 프록시가 헤더를 바꿔도 동작한다.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=utf-8''([^;]+)/i.exec(header)?.[1]?.trim();
  if (star) {
    try {
      return decodeURIComponent(star);
    } catch {
      return star;
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1]?.trim() ?? null;
}

export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}
